import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { resetPassword } from '../../services/auth'; // Adjust path if needed
import { useProfile } from "../../context/ProfileContext";
import toast from 'react-hot-toast';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false); // To show a success message
  const { isDarkMode } = useProfile();

  const handleSendLink = async () => {
    if (!email) {
      return toast.error("Please enter your email address.");
    }
    setIsLoading(true);
    try {
      await resetPassword(email);
      setIsSent(true); // Switch to the success view
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset the modal state when it closes
  const handleClose = () => {
    setEmail('');
    setIsSent(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            /* ✅ UPDATED CLASSES: Using Zinc-900 and Zinc-800 for consistent grey depth */
            className={`w-full max-w-sm p-8 rounded-3xl shadow-2xl transition-all duration-500 border ${
              isDarkMode 
                ? "bg-zinc-900 border-zinc-800 text-white" 
                : "bg-white border-slate-100 text-slate-900"
            }`}
            initial={{ scale: 0.9, y: 20 }} 
            animate={{ scale: 1, y: 0 }} 
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isSent ? (
              /* --- SUCCESS VIEW --- */
              <div className="text-center space-y-6">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? "bg-brand-pink/20" : "bg-green-100"}`}>
                  <span className={`text-2xl ${isDarkMode ? "text-brand-pink" : "text-green-600"}`}>✓</span>
                </div>
                <h2 className="text-2xl font-black uppercase italic tracking-tighter">Recovery Email Sent</h2>
                <p className={`text-sm leading-relaxed ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Please check your Spam Inbox for a reset link sent to <br/>
                  <span className="font-bold text-brand-pink">{email}</span>
                </p>
                <button 
                  onClick={handleClose} 
                  className={`w-full py-4 font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 ${
                    isDarkMode ? "bg-brand-pink text-white" : "bg-blue-600 text-white"
                  }`}
                >
                  Return to Login
                </button>
              </div>
            ) : (
              /* --- INPUT VIEW --- */
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-2xl font-black uppercase italic tracking-tighter">Account Recovery</h2>
                  <p className={`text-sm leading-relaxed ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                    Enter your email to receive a secure recovery link.
                  </p>
                </div>

                <div className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email Address"
                    className={`w-full p-4 rounded-xl outline-none border transition-all ${
                      isDarkMode 
                        ? "bg-brand-gray/10 border-brand-gray text-white focus:border-brand-pink" 
                        : "bg-slate-50 border-slate-200 text-slate-900 focus:border-blue-500"
                    }`}
                  />

                  <button 
                    onClick={handleSendLink} 
                    disabled={isLoading} 
                    className={`w-full py-4 font-black uppercase tracking-widest rounded-xl shadow-xl transition-all active:scale-95 disabled:opacity-50 ${
                      isDarkMode ? "bg-brand-pink text-white" : "bg-blue-600 text-white"
                    }`}
                  >
                    {isLoading ? 'Transmitting...' : 'Send Recovery Link'}
                  </button>

                  <button 
                    onClick={handleClose} 
                    className={`w-full py-2 text-[12px] font-black uppercase tracking-widest transition-colors ${
                      isDarkMode ? "text-zinc-400 hover:text-white" : "text-slate-400 hover:text-slate-900"
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ForgotPasswordModal;